import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, BUTTON_TYPES, RichText, User } from '_atoms';
import { Typography } from '_atoms/Typography';
import { Status, STATUS_TYPES } from '_atoms/Status';
import { dateFormatter } from 'helpers/dateFormatter';
import { ReactComponent as Edit } from 'dist/icons/edit.svg';
import type { FeedbackData } from './types';

interface FeedbackProps {
  feedback: FeedbackData;
  onEdit: () => void;
  onPostFeedback: (data: Partial<FeedbackData>) => void;
  showEdit?: boolean;
}

export const Feedback: React.FC<FeedbackProps> = ({ feedback, onEdit, onPostFeedback, showEdit }) => {
  const { t } = useTranslation();

  return (
    <div className="feedback-info">
      <div className="feedback-info__header">
        <User
          className="feedback-info__user"
          avatar={feedback.approver.profile?.avatar}
          fullName={feedback.approver.full_name}
          roles={feedback.approver.roles}
        />
        <div className="feedback-info__info">
          {feedback.is_draft && <Status>{t('translation:feedback.draft')}</Status>}
          <Typography.Label className="created-at">
            {dateFormatter(feedback.updated_at, 'MMM DD, YYYY [at] HH:mm')}
          </Typography.Label>
          {showEdit && <Button onClick={onEdit} icon={<Edit />} />}
        </div>
      </div>
      <div className="feedback-info__content">
        {feedback.positive !== null && (
          <>
            <Typography.Label>{t('translation:feedback.decision')}</Typography.Label>
            <Status type={feedback.positive ? STATUS_TYPES.GREEN : STATUS_TYPES.RED}>
              {feedback.positive ? t('translation:feedback.approved') : t('translation:feedback.rejected')}
            </Status>
          </>
        )}
        {feedback.english_level && (
          <>
            <Typography.Label>{t('translation:feedback.assumed_english_level')}</Typography.Label>
            <Typography.Text>{feedback.english_level}</Typography.Text>
          </>
        )}
        {feedback.level && (
          <>
            <Typography.Label>{t('translation:feedback.assumed_experience_level')}</Typography.Label>
            <Typography.Text>{feedback.level}</Typography.Text>
          </>
        )}
        <Typography.Label>{t('translation:feedback.comment')}</Typography.Label>
        <RichText text={feedback.comment} />
        {showEdit && feedback.is_draft && (
          <div className="actions">
            <Button onClick={() => onPostFeedback({ uuid: feedback.uuid })}>
              {t('translation:feedback.clear_draft')}
            </Button>
            <Button type={BUTTON_TYPES.PRIMARY} onClick={() => onPostFeedback(feedback)}>
              {t('translation:feedback.post_feedback')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
